import { useState, useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getRoutePath } from "../../routes";
import { useIsMobile } from "../../hooks/is-mobile";
import {
  AUTO_INVOICES_CREATE,
  CREDIT_NOTES_CREATE,
  DEBIT_NOTES_CREATE,
  INVOICES_CREATE,
  RECEIPTS_CREATE,
  REMISSION_NOTES_CREATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import HasAnyPermissionComponent from "../permissions/HasAnyPermissionComponent";

interface FABOption {
  label: string;
  icon: string;
  pathLabel: string;
  action: () => void;
  permission: string;
}

export default function FloatingButtonExpanded() {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);

  const fabOptions: FABOption[] = [
    {
      label: "Factura",
      icon: "fa-file-invoice",
      pathLabel: "addInvoice",
      action: () => {
        history.push(getRoutePath("addInvoice"));
        setIsExpanded(false);
      },
      permission: INVOICES_CREATE,
    },
    {
      label: "Nota de Crédito",
      icon: "fa-file-invoice-dollar",
      pathLabel: "addCreditNote",
      action: () => {
        history.push(getRoutePath("addCreditNote"));
        setIsExpanded(false);
      },
      permission: CREDIT_NOTES_CREATE,
    },
    {
      label: "Nota de Débito",
      icon: "fa-file-invoice-dollar",
      pathLabel: "addDebitNote",
      action: () => {
        history.push(getRoutePath("addDebitNote"));
        setIsExpanded(false);
      },
      permission: DEBIT_NOTES_CREATE,
    },
    {
      label: "Nota de Remisión",
      icon: "fa-file-invoice",
      pathLabel: "addRemissionNote",
      action: () => {
        history.push(getRoutePath("addRemissionNote"));
        setIsExpanded(false);
      },
      permission: REMISSION_NOTES_CREATE,
    },
    {
      label: "AutoFactura",
      icon: "fa-file-invoice",
      pathLabel: "addAutoInvoice",
      action: () => {
        history.push(getRoutePath("addAutoInvoice"));
        setIsExpanded(false);
      },
      permission: AUTO_INVOICES_CREATE,
    },
    {
      label: "Recibo",
      icon: "fa-receipt",
      pathLabel: "addReceipt",
      action: () => {
        history.push(getRoutePath("addReceipt"));
        setIsExpanded(false);
      },
      permission: RECEIPTS_CREATE,
    },
  ];

  // Cerrar FAB cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isMobile) return null;

  return (
    <HasAnyPermissionComponent
      permissionNames={fabOptions.map((option) => option.permission)}
    >
    <div
      ref={fabRef}
      style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 1050 }}
    >
      {/* Backdrop */}
      {isExpanded && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: -1,
          }}
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Extended Options */}
      <div
        style={{
          position: "absolute",
          bottom: "72px", // Espaciado desde el FAB principal
          right: "0",
          display: "flex",
          alignItems: "end",
          flexDirection: "column",
          gap: "12px",
          opacity: isExpanded ? 1 : 0,
          transform: `translateY(${isExpanded ? "0" : "20px"})`,
          visibility: isExpanded ? "visible" : "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {fabOptions
          .filter(
            (option) => location.pathname !== getRoutePath(option.pathLabel)
          )
          .map((option, index) => {
            return (
              <PermissionRequiredComponent
                permissionName={option.permission}
                key={option.label}
              >
                <div
                  key={option.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  {/* Label */}
                  <div
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                      opacity: isExpanded ? 1 : 0,
                      transform: `translateX(${isExpanded ? "0" : "10px"})`,
                      transition: "all 0.2s ease",
                      transitionDelay: `${index * 50 + 100}ms`,
                    }}
                  >
                    {option.label}
                  </div>

                  {/* Mini FAB */}
                  <button
                    onClick={option.action}
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#006657",
                      color: "white",
                      border: "none",
                      borderRadius: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      transform: `scale(${isExpanded ? 1 : 0.8})`,
                      opacity: isExpanded ? 1 : 0,
                      transitionDelay: `${index * 50}ms`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "#004d42";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "#006657";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <i
                      className={`fa ${option.icon}`}
                      style={{ fontSize: "16px" }}
                    />
                  </button>
                </div>
              </PermissionRequiredComponent>
            );
          })}
      </div>

      {/* Main FAB */}
      <button
        onClick={toggleExpanded}
        style={{
          width: "56px",
          height: "56px",
          backgroundColor: "#006657",
          color: "white",
          border: "none",
          borderRadius: "28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          outline: "none",
          transform: `rotate(${isExpanded ? "45deg" : "0deg"})`,
          position: "relative",
        }}
        onMouseEnter={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.backgroundColor =
              "#004d42";
            e.currentTarget.style.transform = "scale(1.05) rotate(0deg)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.backgroundColor =
              "#006657";
            e.currentTarget.style.transform = "scale(1) rotate(0deg)";
          }
        }}
      >
        <i
          className="fa fa-plus"
          style={{
            fontSize: "20px",
            transition: "transform 0.3s ease",
          }}
        />

        {/* Label "New" que aparece al hacer hover cuando está cerrado */}
        <div
          style={{
            position: "absolute",
            right: "64px",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "500",
            whiteSpace: "nowrap",
            opacity: 0,
            transform: "translateX(10px)",
            transition: "all 0.2s ease",
            pointerEvents: "none",
          }}
          className="fab-tooltip"
        >
          Nuevo
        </div>
      </button>

      <style>{`
        .floating-action-button:hover .fab-tooltip {
          opacity: 1;
          transform: translateX(0);
        }
      `}</style>
    </div>
    </HasAnyPermissionComponent>
  );
}
